import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Pagination from "react-js-pagination";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactSelect from "react-select";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import NotFound from "../../components/not-found/NotFound";
import Switch from "../../components/Switch/Switch";
import { IoSearch } from "react-icons/io5";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "../../components/Button/IconButton";
import { toastMessage } from "../../utils/toastMessage";
import common from "../../routes/common";
import scrollToUp from "../../utils/scrollToUp";
import findSerialNumber from "../../utils/findSerialNumber";
import ModalBox from "../../components/modal/ModalBox";
import { Input, Select } from "../../components/Form";
import useButtonLoader from "../../hooks/UseButtonLoader";
import {
  fetchOfferRuleList,
  createOfferRule,
  updateOfferRule,
  fetchOfferVariantList,
} from "../../services/offerEngine";
import { fetchConsultantListApi } from "../../services/consultantManagement";
import { getUserListApi } from "../../services/userManagement";
import JoditEditor from "jodit-react";

const { NOT_FOUND } = common;

function deviceSummary(userAgent = "") {
  const agent = String(userAgent || "");
  const os = /Windows/i.test(agent) ? "Windows"
    : /Mac OS|Macintosh/i.test(agent) ? "Mac"
    : /Android/i.test(agent) ? "Android"
    : /iPhone|iPad/i.test(agent) ? "iOS"
    : "";
  const browser = /Edg\//i.test(agent) ? "Edge"
    : /Chrome\//i.test(agent) ? "Chrome"
    : /Firefox\//i.test(agent) ? "Firefox"
    : /Safari\//i.test(agent) ? "Safari"
    : "";
  return [os, browser].filter(Boolean).join(" · ");
}

function formatAuditTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("en-IN", { hour12: true, timeZone: "Asia/Kolkata" });
}

const emptyRule = {
  name: "",
  description: "",
  variants: [],
  trigger: { min: 0, max: 999999 },
  frequency: { maxShowCount: 999999, maxUseCount: 1 },
  userSegments: [],
  targetUsers: [],
  consultants: { type: "ALL", ids: [] },
  consultantStatus: "ALL",
  applicableModes: ["CHAT", "CALL"],
  display: { type: "SINGLE", priority: 0 },
  validity: { startAt: "", endAt: "" },
  timeWindow: { enabled: false, start: "", end: "", unlimitedUses: false },
  isActive: true,
};

const formatClockLabel = (hm) => {
  if (!hm || !hm.includes(":")) return "";
  const [hRaw, mRaw] = hm.split(":");
  let h = Number(hRaw);
  const m = String(mRaw || "00").padStart(2, "0");
  if (Number.isNaN(h)) return hm;
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${suffix}`;
};

const CONSULTANT_STATUS_OPTS = [
  { value: "ONLINE", label: "Online" },
  { value: "OFFLINE", label: "Offline" },
  { value: "ALL", label: "All" },
];

const MODE_MULTI_OPTS = [
  { value: "CHAT", label: "Chat" },
  { value: "CALL", label: "Call" },
];

const DISPLAY_TYPE_OPTS = [
  { value: "SINGLE", label: "Single" },
  { value: "MULTIPLE", label: "Multiple" },
];

const CONSULTANT_TYPE_OPTS = [
  { value: "ALL", label: "All Consultants" },
  { value: "SELECTED", label: "Selected Consultants" },
];

// ─── Predefined User Segments ────────────────────────────────────────
// Labels in human-readable Hindi/English mix matching product spec.
const USER_SEGMENT_OPTS = [
  // ── Recharge ──────────────────────────────────────────────────────
  {
    label: "🔋 Recharge Segments",
    options: [
      { value: "RECHARGE_NO_CONSULT_SAME_DAY", label: "Recharge kiya – same day 1st consultation nahi liya" },
      { value: "RECHARGE_NEVER_CONSULTED", label: "Recharge kiya – abhi tak kabhi consult nahi kiya" },
      { value: "RECHARGE_50PCT_REMAINING_72H", label: "Recharge ke 72h baad bhi 50%+ amount wallet mein hai" },
      { value: "RECHARGE_50PCT_REMAINING_AFTER_72H", label: "72h ke baad wallet mein 50% amount bacha hua hai" },
      { value: "RECHARGE_100RS_NO_CONSULT_SAME_DAY", label: "100+ Rs recharge – same day koi consultation nahi" },
      { value: "WALLET_200_PLUS", label: "Wallet mein 200+ coins available" },
    ],
  },
  {
    label: "🆕 User Registration",
    options: [
      { value: "REGISTRATION_24H", label: "Registered in last 24 hours" },
      { value: "REGISTRATION_48H", label: "Registered in last 48 hours" },
    ],
  },
  // ── Consultation Count ─────────────────────────────────────────────
  {
    label: "🗓️ Consultation Gap",
    options: [
      { value: "NO_CONSULT_7D", label: "Last 7 days mein koi consultation nahi" },
      { value: "NO_CONSULT_14D", label: "Last 14 days mein koi consultation nahi" },
      { value: "NO_CONSULT_21D", label: "Last 21 days mein koi consultation nahi" },
      { value: "NO_CONSULT_30D", label: "Last 30 days mein koi consultation nahi" },
    ],
  },
  // ── Missed / Cancelled ─────────────────────────────────────────────
  {
    label: "❌ Missed & Cancelled",
    options: [
      { value: "MULTIPLE_FAILED_24H", label: "24h mein multiple consultation attempt – par consult nahi hua" },
    ],
  },
  // ── Inactivity ─────────────────────────────────────────────────────
  {
    label: "💤 Inactivity (No App Open)",
    options: [
      { value: "INACTIVE_7D", label: "Signup ke 7 days ke ander app open nahi kiya" },
      { value: "INACTIVE_14D", label: "Signup ke 14 days ke ander app open nahi kiya" },
      { value: "INACTIVE_21D", label: "Signup ke 21 days ke ander app open nahi kiya" },
      { value: "INACTIVE_30D", label: "Signup ke 30 days ke ander app open nahi kiya" },
    ],
  },
];

// Flat map for easy lookup when editing
const ALL_SEGMENT_OPTS = USER_SEGMENT_OPTS.flatMap((g) => g.options);

const OfferRuleList = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  const navigate = useNavigate();
  const activePage = +searchParam.get("page") || 1;
  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search, 300);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const editor = useRef(null);
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Enter rule description...",
      toolbar: true,
      spellcheck: true,
      language: "en",
      toolbarButtonSize: "medium",
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      uploader: {
        insertImageAsBase64URI: true,
      },
    }),
    []
  );

  // variant options for multi-select
  const [variantOptions, setVariantOptions] = useState([]);

  // modal
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({ ...emptyRule });
  const [error, setError] = useState({});
  const [buttonRef, buttonLoader] = useButtonLoader("Save");

  // consultant options for multi-select
  const [consultantOptions, setConsultantOptions] = useState([]);

  // user search options for targetUsers
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // ─── Load variant options ─────────────────────────────────────────
  const loadVariants = useCallback(async () => {
    try {
      const { data } = await fetchOfferVariantList({ limit: 200, isActive: "true" });
      if (data.code === 200) {
        setVariantOptions(
          (data.result?.list || []).map((v) => ({
            value: v._id,
            label: `${v.name} (${v.type} · ${v.mode} · ₹${v.price})`,
          }))
        );
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadVariants(); }, [loadVariants]);

  // ─── Load active consultants ───────────────────────────────────────
  const loadConsultants = useCallback(async () => {
    try {
      const { data } = await fetchConsultantListApi({ page: "1", limit: "500", filter: { status: true } });
      if (data?.code === 200) {
        setConsultantOptions(
          (data.result?.data || data.result?.list || []).map((c) => ({
            value: c._id,
            label: `${c.accountName || c.firstName || ""} ${c.lastName || ""}`.trim() + (c.phone ? ` (${c.phone})` : ""),
          }))
        );
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadConsultants(); }, [loadConsultants]);

  // ─── Search users for targetUsers (async on query) ─────────────────
  const searchUsers = useCallback(async (query = "") => {
    try {
      if (query.length < 2) return;
      setUserSearchLoading(true);
      // page & limit MUST be strings – the Joi validator rejects numbers
      const { data } = await getUserListApi({ page: "1", limit: "30", search: query });
      if (data?.code === 200) {
        setUserOptions(
          (data.result?.data || data.result?.list || []).map((u) => ({
            value: u._id,
            label: `${u.details?.name || u.name || "User"} · ${u.details?.phone || u.phone || ""} - ${u.userId || ""}`,
          }))
        );
      }
    } catch (e) { console.error(e); }
    finally { setUserSearchLoading(false); }
  }, []);

  // Pre-populate user list when modal opens (bypasses the 2-char guard)
  const loadInitialUsers = useCallback(async () => {
    try {
      setUserSearchLoading(true);
      const { data } = await getUserListApi({ page: "1", limit: "30" });
      if (data?.code === 200) {
        setUserOptions(
          (data.result?.data || data.result?.list || []).map((u) => ({
            value: u._id,
            label: `${u.details?.name || u.name || "User"} · ${u.details?.phone || u.phone || ""} - ${u.userId || ""}`,
          }))
        );
      }
    } catch (e) { console.error(e); }
    finally { setUserSearchLoading(false); }
  }, []);

  useEffect(() => { if (showModal) loadInitialUsers(); }, [showModal]);

  // ─── Fetch rules ─────────────────────────────────────────────────
  const fetchList = async () => {
    try {
      showLoader();
      const params = { page: activePage, limit: 10 };
      if (debounceValue) { params.search = debounceValue; params.page = 1; }
      const { data } = await fetchOfferRuleList(params);
      if (data.code === 200) {
        setList(data.result?.list || []);
        setTotal(data.result?.total || 0);
        scrollToUp();
      } else { setList([]); setTotal(0); toastMessage(data.message, "error", "ruleList"); }
    } catch (e) { console.error(e); }
    finally { hideLoader(); }
  };

  useEffect(() => { fetchList(); }, [activePage, debounceValue]);

  const handlePageChange = (page) => { searchParam.set("page", page); setSearchParam(searchParam); };

  // ─── Modal helpers ────────────────────────────────────────────────
  const openAdd = () => {
    setIsEdit(false);
    setFormData({ ...emptyRule, variants: [], userSegments: [], applicableModes: ["CHAT", "CALL"] });
    setError({});
    setShowModal(true);
  };

  const openEdit = (item) => {
    setIsEdit(true);
    const variants = (item.variants || []).map((v) =>
      typeof v === "object" ? v._id : v
    );
    setFormData({
      ...emptyRule,
      ...item,
      variants,
      validity: {
        startAt: item.validity?.startAt ? item.validity.startAt.slice(0, 16) : "",
        endAt: item.validity?.endAt ? item.validity.endAt.slice(0, 16) : "",
      },
      timeWindow: {
        enabled: !!item.timeWindow?.enabled,
        start: item.timeWindow?.start || "",
        end: item.timeWindow?.end || "",
        unlimitedUses: !!item.timeWindow?.unlimitedUses,
      },
    });
    setError({});
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setFormData({ ...emptyRule }); setError({}); };

  // generic field updater supporting nested keys like "trigger.min"
  const onFieldChange = (path, value) => {
    setFormData((prev) => {
      const copy = { ...prev };
      const parts = path.split(".");
      if (parts.length === 2) {
        copy[parts[0]] = { ...copy[parts[0]], [parts[1]]: value };
      } else {
        copy[path] = value;
      }
      return copy;
    });
  };

  // ─── Selected segment values for ReactSelect ─────────────────────
  const selectedSegmentValues = ALL_SEGMENT_OPTS.filter((o) =>
    (formData.userSegments || []).includes(o.value)
  );

  // ─── Validation ───────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.name) errs.name = "Required";
    if (formData.timeWindow?.enabled) {
      if (!formData.timeWindow.start || !formData.timeWindow.end) {
        errs.timeWindow = "Start and end time are required";
      } else if (formData.timeWindow.start === formData.timeWindow.end) {
        errs.timeWindow = "Start and end time must be different";
      }
    }
    setError(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      buttonLoader(true);
      const payload = {
        ...formData,
        trigger: { min: Number(formData.trigger.min), max: Number(formData.trigger.max) },
        frequency: { maxShowCount: Number(formData.frequency.maxShowCount), maxUseCount: Number(formData.frequency.maxUseCount) },
        display: { ...formData.display, priority: Number(formData.display.priority) },
        timeWindow: {
          enabled: !!formData.timeWindow?.enabled,
          start: formData.timeWindow?.enabled ? (formData.timeWindow.start || "") : "",
          end: formData.timeWindow?.enabled ? (formData.timeWindow.end || "") : "",
          unlimitedUses: !!formData.timeWindow?.unlimitedUses,
        },
      };
      const apiFn = isEdit ? updateOfferRule : createOfferRule;
      const { data } = await apiFn(payload);
      if (data.code === 200) { toastMessage(data.message, "success", "ruleSave"); closeModal(); fetchList(); }
      else toastMessage(data.message, "error", "ruleSave");
    } catch (e) { console.error(e); }
    finally { buttonLoader(false); }
  };

  // ─── Toggle ───────────────────────────────────────────────────────
  const toggleActive = async (item) => {
    try {
      const { data } = await updateOfferRule({ _id: item._id, isActive: !item.isActive });
      if (data.code === 200) { toastMessage("Status updated", "success", "ruleStatus"); fetchList(); }
      else toastMessage(data.message, "error", "ruleStatus");
    } catch (e) { console.error(e); }
  };

  // ─── Selected variant values for ReactSelect ─────────────────────
  const selectedVariantValues = variantOptions.filter((o) =>
    formData.variants.includes(o.value)
  );

  const selectedModeValues = MODE_MULTI_OPTS.filter((o) =>
    formData.applicableModes?.includes(o.value)
  );

  const selectedConsultantValues = consultantOptions.filter((o) =>
    (formData.consultants?.ids || []).includes(o.value)
  );

  const selectedTargetUserValues = userOptions.filter((o) =>
    (formData.targetUsers || []).map(String).includes(String(o.value))
  );

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Offer Management</h4>
            <div className="block_user_button">
              <button onClick={openAdd}><AddIcon /> Add Rule</button>
              <button onClick={() => navigate('/offer-engine/variants')}><AddIcon /> Add Variant</button>
            </div>
          </div>

          <div className="FilterBox FilterArea">
            <div className="form-group search_lg_size userSearch">
              <input type="search" className={`form-control ${search && "active"}`} placeholder="Search by name" value={search} style={{ "--color": "#FF8989" }} onChange={(e) => setSearch(e.target.value)} />
              <span className="user_search_icon"><IoSearch /></span>
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Name</th>
                  <th>Variants</th>
                  <th>Modes</th>
                  <th>Priority</th>
                  <th>Validity</th>
                  <th>Daily Time</th>
                  <th>Created by</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.length > 0 ? (
                  list.map((item, i) => (
                    <tr key={item._id}>
                      <td>{findSerialNumber(i, activePage, 10)}</td>
                      <td>{item.name}</td>
                      <td>{(item.variants || []).map((v) => v.name || "—").join(", ") || "—"}</td>
                      <td>{(item.applicableModes || []).join(", ")}</td>
                      <td>{item.display?.priority ?? 0}</td>
                      <td style={{ fontSize: "12px" }}>
                        {item.validity?.startAt ? new Date(item.validity.startAt).toLocaleString("en-IN", { hour12: true, timeZone: 'Asia/Kolkata' }) : "—"}
                        {" → "}
                        {item.validity?.endAt ? new Date(item.validity.endAt).toLocaleString("en-IN", { hour12: true, timeZone: 'Asia/Kolkata' }) : "—"}
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {item.timeWindow?.enabled
                          ? `${formatClockLabel(item.timeWindow.start)} – ${formatClockLabel(item.timeWindow.end)}${item.timeWindow.unlimitedUses ? " · Unlimited" : ""}`
                          : "All day"}
                      </td>
                      <td style={{ fontSize: "12px", minWidth: 160 }}>
                        {item.createdBy?.name || item.createdBy?.email ? (
                          <>
                            <div>{item.createdBy.name || "—"}</div>
                            <div>{item.createdBy.email || ""}</div>
                            <div>{deviceSummary(item.createdBy.userAgent) || "—"}</div>
                            <div>{item.createdBy.ipAddress || ""}</div>
                            <div>{formatAuditTime(item.createdAt)}</div>
                          </>
                        ) : "—"}
                      </td>
                      <td>
                        <div className="action_button">
                          <Switch checked={item.isActive} onChange={() => toggleActive(item)} />
                          <IconButton className="px-2 py-0" onClick={() => openEdit(item)}><EditIcon className="edit_icon" /></IconButton>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <NotFound cols={9} msg="No rules found" icon={NOT_FOUND} loader={loader} />
                )}
              </tbody>
            </table>
          </div>

          {total > 10 && (
            <div className="pagination-wrapper">
              <div className="Pagination">
                <Pagination activePage={activePage} previousLabel="previous" nextLabel="next" itemsCountPerPage={10} totalItemsCount={total} pageRangeDisplayed={5} onChange={handlePageChange} hideDisabled />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ──────────── Add / Edit Modal ──────────── */}
      {showModal && (
        <ModalBox
          status={showModal}
          onClose={closeModal}
          title={isEdit ? "Edit Rule" : "Add Rule"}
          closeIcon
          size="lg"
          custom
          customizedFooter={
            <div className="model_custom_footer">
              <button className="btn_size" ref={buttonRef} onClick={handleSubmit}>Save</button>
            </div>
          }
          category
        >
          <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "8px" }}>
            {/* Rule Name */}
            <div className="form-group mb-2">
              <Input type="text" label="Rule Name" placeholder="Enter rule name" className="form-control" value={formData.name} onChange={(e) => onFieldChange("name", e.target.value)} error={!formData.name && error.name} required maxLength={80} />
            </div>
            <div className="form-group my-2">
              <label className="form-label">Description</label>
              <JoditEditor
                ref={editor}
                value={formData.description || ""}
                tabIndex={1}
                onBlur={(newContent) => {
                  onFieldChange("description", newContent);
                }}
                config={editorConfig}
              />
            </div>

            {/* Variants (multi-select) */}
            <div className="form-group mb-2">
              <label className="form-label">Select Variants <span style={{ color: "red" }}>*</span></label>
              <ReactSelect
                isMulti
                options={variantOptions}
                value={selectedVariantValues}
                onChange={(selected) => onFieldChange("variants", (selected || []).map((s) => s.value))}
                placeholder="Choose variants…"
                closeMenuOnSelect={false}
              />
            </div>

            {/* Trigger */}
            <div style={{ display: "flex", gap: "12px" }}>
              <div className="form-group mb-2" style={{ flex: 1 }}>
                <Input type="number" label="Min Consultation Count" className="form-control" value={formData.trigger.min} onChange={(e) => onFieldChange("trigger.min", e.target.value)} />
              </div>
              <div className="form-group mb-2" style={{ flex: 1 }}>
                <Input type="number" label="Max Consultation Count" className="form-control" value={formData.trigger.max} onChange={(e) => onFieldChange("trigger.max", e.target.value)} />
              </div>
            </div>

            {/* User Segments – predefined dropdown */}
            <div className="form-group mb-2">
              <label className="form-label">
                User Segments
                <span style={{ fontSize: "11px", color: "#888", marginLeft: "6px" }}>
                  (Leave empty to target all users)
                </span>
              </label>
              <ReactSelect
                isMulti
                options={USER_SEGMENT_OPTS}
                value={selectedSegmentValues}
                onChange={(selected) =>
                  onFieldChange("userSegments", (selected || []).map((s) => s.value))
                }
                placeholder="Choose user segments…"
                closeMenuOnSelect={false}
                noOptionsMessage={() => "No segments found"}
                styles={{
                  groupHeading: (base) => ({
                    ...base,
                    fontSize: "11px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    color: "#555",
                    background: "#f5f5f5",
                    padding: "6px 12px",
                  }),
                  multiValue: (base) => ({
                    ...base,
                    background: "#e8f0fe",
                    borderRadius: "10px",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#1a56db",
                    fontSize: "12px",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#1a56db",
                    ':hover': { background: "#c7d9fb", color: "#1a56db" },
                  }),
                }}
              />
            </div>

            {/* Target Specific Users (whitelist) */}
            <div className="form-group mb-2">
              <label className="form-label">
                🎯 Target Specific Users
                <span style={{ fontSize: "11px", color: "#888", marginLeft: "6px" }}>
                  (Leave empty to target all eligible users)
                </span>
              </label>
              <ReactSelect
                isMulti
                isLoading={userSearchLoading}
                options={userOptions}
                value={selectedTargetUserValues}
                onChange={(selected) =>
                  onFieldChange("targetUsers", (selected || []).map((s) => s.value))
                }
                onInputChange={(val) => { searchUsers(val); }}
                placeholder="Search by name or phone…"
                filterOption={() => true}
                closeMenuOnSelect={false}
                noOptionsMessage={() => userSearchLoading ? "Searching…" : "No users found"}
                styles={{
                  multiValue: (base) => ({
                    ...base,
                    background: "#fff3e0",
                    borderRadius: "10px",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "#e65100",
                    fontSize: "12px",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "#e65100",
                    ':hover': { background: "#ffe0b2", color: "#e65100" },
                  }),
                }}
              />
            </div>

            {/* Frequency */}
            <div style={{ display: "flex", gap: "12px" }}>
              {/* <div className="form-group mb-2" style={{ flex: 1 }}>
                <Input type="number" label="Max Show Count" className="form-control" value={formData.frequency.maxShowCount} onChange={(e) => onFieldChange("frequency.maxShowCount", e.target.value)} />
              </div> */}
              <div className="form-group mb-2" style={{ flex: 1 }}>

                <Select label="Consultant Status" className="form-control" value={formData.consultantStatus} onChange={(e) => onFieldChange("consultantStatus", e.target.value)}>
                  {CONSULTANT_STATUS_OPTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                </Select>
              </div>
              <div className="form-group mb-2" style={{ flex: 1 }}>
                <Input type="number" label="Max Use Count" className="form-control" value={formData.frequency.maxUseCount} onChange={(e) => onFieldChange("frequency.maxUseCount", e.target.value)} />
              </div>
            </div>


            {/* Consultant Type */}
            <div className="form-group mb-2">
              <Select label="Consultant Selection" className="form-control" value={formData.consultants?.type || "ALL"} onChange={(e) => onFieldChange("consultants", { ...formData.consultants, type: e.target.value })}>
                {CONSULTANT_TYPE_OPTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </Select>
            </div>

            {formData.consultants?.type === "SELECTED" && (
              <div className="form-group mb-2">
                <label className="form-label">Select Consultants</label>
                <ReactSelect
                  isMulti
                  options={consultantOptions}
                  value={selectedConsultantValues}
                  onChange={(selected) =>
                    onFieldChange("consultants", {
                      ...formData.consultants,
                      ids: (selected || []).map((s) => s.value),
                    })
                  }
                  placeholder="Search & select consultants…"
                  closeMenuOnSelect={false}
                  noOptionsMessage={() => "No active consultants found"}
                />
              </div>
            )}

            {/* Consultant Status */}
            <div className="form-group mb-2">

            </div>

            {/* Applicable Modes multi-select */}
            <div className="form-group mb-2">
              <label className="form-label">Applicable Modes</label>
              <ReactSelect
                isMulti
                options={MODE_MULTI_OPTS}
                value={selectedModeValues}
                onChange={(selected) => onFieldChange("applicableModes", (selected || []).map((s) => s.value))}
                placeholder="Choose modes…"
              />
            </div>

            {/* Display */}
            <div style={{ display: "flex", gap: "12px" }}>
              <div className="form-group mb-2" style={{ flex: 1 }}>
                <Select label="Display Type" className="form-control" value={formData.display?.type || "SINGLE"} onChange={(e) => onFieldChange("display.type", e.target.value)}>
                  {DISPLAY_TYPE_OPTS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                </Select>
              </div>
              <div className="form-group mb-2" style={{ flex: 1 }}>
                <Input type="number" label="Priority" className="form-control" value={formData.display?.priority ?? 0} onChange={(e) => onFieldChange("display.priority", e.target.value)} />
              </div>
            </div>

            <div className="form-group mb-2" style={{ borderTop: "1px solid #eee", paddingTop: "12px" }}>
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={!!formData.timeWindow?.enabled}
                  onChange={(e) => onFieldChange("timeWindow.enabled", e.target.checked)}
                />
                Daily time range (optional, India time)
              </label>
              <div style={{ fontSize: "12px", color: "#888", marginBottom: "8px" }}>
                Leave off to keep this offer available all day. A range that crosses midnight is allowed, for example 3:00 PM to 12:00 AM.
              </div>
              {formData.timeWindow?.enabled && (
                <div style={{ display: "flex", gap: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">From</label>
                    <input type="time" className="form-control" value={formData.timeWindow?.start || ""} onChange={(e) => onFieldChange("timeWindow.start", e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">To</label>
                    <input type="time" className="form-control" value={formData.timeWindow?.end || ""} onChange={(e) => onFieldChange("timeWindow.end", e.target.value)} />
                  </div>
                </div>
              )}
              {error.timeWindow && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>{error.timeWindow}</div>}
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" }}>
                <input
                  type="checkbox"
                  checked={!!formData.timeWindow?.unlimitedUses}
                  onChange={(e) => onFieldChange("timeWindow.unlimitedUses", e.target.checked)}
                />
                Unlimited uses (ignore Max Use Count)
              </label>
            </div>

            {/* Validity */}
            <div style={{ display: "flex", gap: "12px" }}>
              <div className="form-group mb-2" style={{ flex: 1 }}>
                <label className="form-label">Start Date</label>
                <input type="datetime-local" className="form-control" value={formData.validity?.startAt || ""} onChange={(e) => onFieldChange("validity.startAt", e.target.value)} />
              </div>
              <div className="form-group mb-2" style={{ flex: 1 }}>
                <label className="form-label">End Date</label>
                <input type="datetime-local" className="form-control" value={formData.validity?.endAt || ""} onChange={(e) => onFieldChange("validity.endAt", e.target.value)} />
              </div>
            </div>
          </div>
        </ModalBox>
      )}
    </>
  );
};

export default OfferRuleList;
